// Travel projects — journeys treated as first-class projects (Traveler Mode).
//
// Content is written from the owner's own spoken account (2026-07 transcripts).
// Dates marked `datesFromLog: true` come from the GPS logs in
// references/kmz_files. Route JSONs live in src/data/routes/; `routeKind`
// distinguishes recorded GPS tracks from hand-drawn approximations.
// `description_ja/en` are paragraph arrays (polite, literary register by the
// owner's request). Tags carry a `cat` for color coding:
//   vehicle (移動手段) / stay (泊まり方) / region (国内・海外) / theme (テーマ)
//
// Photos under public/images/travels/ were picked from the owner's shared
// Google Photos albums (use granted); `albumUrl` links to the full album.

const TRAVEL_PROJECTS = [
  {
    id: 'shikoku-cycling',
    sortKey: '2012-08',
    vehicle: 'bicycle',
    title_ja: '四国一周 自転車旅',
    title_en: 'Around Shikoku by Bicycle',
    period_ja: '2012年夏（中3）・7泊8日',
    period_en: 'Summer 2012 (age 14) · 8 days',
    albumUrl: '',
    images: [],
    description_ja: [
      'すべての旅は、この夏から始まりました。中学3年の夏休み、友達2人と自転車にテントを積み、7泊8日で四国をひとまわり。神戸からジャンボフェリーで高松へ渡り、時計回りに徳島、高知、愛媛と走りました。',
      '嵐の夜には、造船所の方が倉庫に泊めてくださいました。桂浜では、おばあちゃんの土佐弁がまるで聞き取れません。同じ国の中に、こんなに知らない世界がある——。',
      '足摺岬のあたりでは、何時間も漕いでようやく次の街が見えてきます。それは子どもの頃に憧れた、ポケモンの世界そのものでした。「近くの四国でこれだけの大冒険ができるなら、世界に出たらどうなってしまうんだろう」。その予感だけをお土産に、家に帰りました。',
    ],
    description_en: [
      'Every journey since traces back to this one. In the last summer of junior high, two friends and I strapped tents to our bicycles and circled Shikoku in eight days — ferry from Kobe to Takamatsu, then clockwise through Tokushima, Kochi and Ehime.',
      'On a stormy night, a shipyard worker let us sleep in his warehouse. At Katsurahama beach, an old woman spoke a dialect I could not understand at all. So much unknown world, inside my own country.',
      'Near Cape Ashizuri, you pedal for hours before the next town finally rises into view — exactly the Pokémon world I had dreamed of as a boy. If little Shikoku held this much adventure, what would the wide world hold? I carried that question home like a souvenir.',
    ],
    tags: [
      { ja: '自転車', en: 'Bicycle', cat: 'vehicle' },
      { ja: 'テント泊', en: 'Camping', cat: 'stay' },
      { ja: '国内', en: 'Japan', cat: 'region' },
      { ja: '原点', en: 'Origin story', cat: 'theme' },
    ],
    route: 'shikoku-cycling',
    routeKind: 'approx',
    datesFromLog: false,
    cities: [
      { ja: '高松', en: 'Takamatsu', lon: 134.05, lat: 34.35 },
      { ja: '鳴門', en: 'Naruto', lon: 134.61, lat: 34.17 },
      { ja: '徳島', en: 'Tokushima', lon: 134.55, lat: 34.07 },
      { ja: '室戸岬', en: 'Cape Muroto', lon: 134.175, lat: 33.25 },
      { ja: '高知', en: 'Kochi', lon: 133.53, lat: 33.56 },
      { ja: '足摺岬', en: 'Cape Ashizuri', lon: 133.02, lat: 32.72 },
      { ja: '土佐清水', en: 'Tosashimizu', lon: 132.955, lat: 32.78, labelFlip: true },
      { ja: '宇和島', en: 'Uwajima', lon: 132.56, lat: 33.22 },
      { ja: '大洲', en: 'Ozu', lon: 132.55, lat: 33.51 },
      { ja: '松山', en: 'Matsuyama', lon: 132.77, lat: 33.84 },
      { ja: '新居浜', en: 'Niihama', lon: 133.28, lat: 33.96 },
      { ja: '観音寺', en: 'Kanonji', lon: 133.66, lat: 34.13 },
    ],
  },
  {
    id: 'tokyo-cycling',
    sortKey: '2016-03',
    vehicle: 'bicycle',
    title_ja: '自転車で上京',
    title_en: 'To Tokyo by Bicycle',
    period_ja: '2016年3月末・4泊5日',
    period_en: 'Late Mar 2016 · 5 days',
    albumUrl: 'https://photos.app.goo.gl/bQSvA6eL9q5yrUS27',
    images: [
      { src: "images/travels/tokyo-cycling/6.jpg", alt_ja: "「上京中」の看板を積んで走る旅装備の自転車", alt_en: "The fully loaded bike on the road with the 'Riding to Tokyo' sign" },
      { src: "images/travels/tokyo-cycling/1.jpg", alt_ja: "「上京中 斑鳩(奈良)⇒東京」と書いた手作りの看板", alt_en: "Handwritten sign reading 'Riding to the capital: Ikaruga (Nara) to Tokyo'" },
      { src: "images/travels/tokyo-cycling/2.jpg", alt_ja: "出発の朝、法隆寺の門前で弟たちと自転車と記念撮影", alt_en: "Departure morning photo with my brothers and the bike at Horyu-ji temple gate" },
      { src: "images/travels/tokyo-cycling/3.jpg", alt_ja: "田園の橋の欄干にもたれるツーリング自転車", alt_en: "The touring bike leaning on a bridge railing over rice fields" },
      { src: "images/travels/tokyo-cycling/4.jpg", alt_ja: "京都・片岡サイクルの店先でビルダーさんと愛車", alt_en: "With the frame builder outside Kataoka Cycles in Kyoto" },
      { src: "images/travels/tokyo-cycling/5.jpg", alt_ja: "夕暮れの琵琶湖畔、守山の静かな湖岸", alt_en: "Quiet Lake Biwa shoreline at dusk near Moriyama" },
      { src: "images/travels/tokyo-cycling/7.jpg", alt_ja: "山あいのダム湖、エメラルド色の水面", alt_en: "An emerald reservoir behind a dam deep in the mountains" },
      { src: "images/travels/tokyo-cycling/8.jpg", alt_ja: "大井川橋の青いトラス橋を背に佇む自転車", alt_en: "The bike resting before the blue truss spans of the Oi River bridge" },
      { src: "images/travels/tokyo-cycling/9.jpg", alt_ja: "雨に霞む御殿場越えの山あいの集落", alt_en: "A misty mountain valley on the rainy climb over the Gotemba pass" },
      { src: "images/travels/tokyo-cycling/10.jpg", alt_ja: "旅の終わり、東京の何もない新居に置かれた自転車", alt_en: "Journey's end: the bike parked in the empty new Tokyo apartment" },
    ],
    description_ja: [
      '大学進学で東京へ。それなら引っ越しも自転車で、と「上京中（奈良）⇒東京」の看板をくくりつけて、斑鳩の実家を漕ぎ出しました。',
      '受験生時代、トイレの隙間時間にこっそり読みふけっていたのが、自転車で世界を旅する崎山さんのブログでした。その方の相棒と同じ、京都・片岡自転車のオーダーメイド「ヴィゴーレ」。まずはその工房に立ち寄って「今から東京へ行ってきます」とご報告してからの出発です。',
      '滋賀の友人宅、岡崎の知人宅、静岡のビジネスホテル、新百合ヶ丘の親戚の家。雨の御殿場越えでは、峠のお蕎麦屋さんのストーブでずぶ濡れの服を乾かしてもらいました。5日目、下北沢の何もない新居に着いて、最初に部屋へ入れた家具は自転車でした。',
    ],
    description_en: [
      'University meant moving to Tokyo — so why not move by bicycle? With a hand-written sign, “Moving up: Nara → Tokyo,” tied to the frame, I pedaled out of my family home in Ikaruga.',
      'As an exam student I had secretly devoured the blog of a cyclist riding around the world; my bike was a custom-built Vigore from the very same Kyoto workshop, Kataoka Cycles. The trip began with a visit there, to report: I am off to Tokyo now.',
      'A friend’s house in Shiga, a family friend’s in Okazaki, a business hotel in Shizuoka, a cousin’s home in Shin-Yurigaoka. Crossing Gotemba in cold rain, a soba shop by the pass let me dry my soaked clothes at their stove. On day five I reached the empty new room in Shimokitazawa — and the first piece of furniture to move in was the bicycle.',
    ],
    tags: [
      { ja: '自転車', en: 'Bicycle', cat: 'vehicle' },
      { ja: '国内', en: 'Japan', cat: 'region' },
      { ja: '引っ越し', en: 'Moving house', cat: 'theme' },
    ],
    route: 'tokyo-cycling',
    routeKind: 'approx',
    datesFromLog: false,
    cities: [
      { ja: '斑鳩（法隆寺）', en: 'Ikaruga (Horyu-ji)', lon: 135.734, lat: 34.614 },
      { ja: '京都', en: 'Kyoto', lon: 135.76, lat: 35.0, labelFlip: true },
      { ja: '守山', en: 'Moriyama', lon: 135.99, lat: 35.06 },
      { ja: '名古屋', en: 'Nagoya', lon: 136.91, lat: 35.18 },
      { ja: '岡崎', en: 'Okazaki', lon: 137.17, lat: 34.95 },
      { ja: '静岡', en: 'Shizuoka', lon: 138.38, lat: 34.98 },
      { ja: '御殿場', en: 'Gotemba', lon: 138.93, lat: 35.31 },
      { ja: '新百合ヶ丘', en: 'Shin-Yurigaoka', lon: 139.51, lat: 35.6, labelFlip: true, labelDy: 10 },
      { ja: '下北沢', en: 'Shimokitazawa', lon: 139.667, lat: 35.661 },
    ],
  },
  {
    id: 'nz-cycling',
    sortKey: '2017-02',
    vehicle: 'bicycle',
    title_ja: 'ニュージーランド南島 自転車縦断',
    title_en: 'New Zealand South Island by Bicycle',
    period_ja: '2017年2月〜3月・約40日',
    period_en: 'Feb–Mar 2017 · ~40 days',
    albumUrl: 'https://photos.app.goo.gl/c2Pua9ukkJWGKtF87',
    images: [
      { src: "images/travels/nz-cycling/1.jpg", alt_ja: "レインボーロードの砂利道、山あいにぽつんと停めた旅の自転車", alt_en: "The touring bike alone on the gravel of the Rainbow Road, deep in the mountains" },
      { src: "images/travels/nz-cycling/2.jpg", alt_ja: "キャンプ場に張ったテントと旅の自転車", alt_en: "The tent pitched at a campsite beside the touring bike" },
      { src: "images/travels/nz-cycling/3.jpg", alt_ja: "夕暮れのキーウィ注意標識と、旅の相棒の自転車", alt_en: "A kiwi-crossing sign and my trusty bike at dusk" },
      { src: "images/travels/nz-cycling/4.jpg", alt_ja: "雪をいただくマウントクックと黄金色の草原", alt_en: "Snow-capped Aoraki / Mt Cook above golden tussock" },
      { src: "images/travels/nz-cycling/5.jpg", alt_ja: "展望台で、旅先の仲間たちと自撮り", alt_en: "A selfie with friends from the road at a lookout" },
      { src: "images/travels/nz-cycling/6.jpg", alt_ja: "クイーンズタウンの湖畔で開かれる週末マーケット", alt_en: "The weekend market on the Queenstown lakefront" },
      { src: "images/travels/nz-cycling/7.jpg", alt_ja: "夜のクイーンズタウンの路上ライブ", alt_en: "Buskers playing on a Queenstown street at night" },
      { src: "images/travels/nz-cycling/8.jpg", alt_ja: "クイーンズタウンの湖畔の公園で、ベンチでひと休みする旅人たち", alt_en: "Travelers resting on a bench in a Queenstown lakeside park" },
      { src: "images/travels/nz-cycling/9.jpg", alt_ja: "ボブズピークから見下ろすクイーンズタウンとワカティプ湖", alt_en: "Queenstown and Lake Wakatipu from Bob's Peak" },
      { src: "images/travels/nz-cycling/10.jpg", alt_ja: "旅のゴール、南島最南端ブラフの道標と自転車", alt_en: "Journey's end: the bike and the signpost at Stirling Point, Bluff" },
    ],
    description_ja: [
      '「人力で世界一周がしたい」。大学1年の終わり、その夢の最初の武者修行として選んだのが、ニュージーランド南島でした。ウェリントンからフェリーで海峡を渡り、約40日かけて島の最南端ブラフまで。',
      '地震と山火事で幹線道路が塞がり、迂回した未舗装のレインボーロードは、時速8kmで120km。対向車は、土煙が見えてから すれ違うまでに1時間という世界です。牛と同じ川の水を飲み、無人の避難小屋ではロウソクの灯りの中、音楽を大音量でかけて心細さをごまかしながら朝を待ちました。',
      'テカポ湖の星空、マウントクックのトレッキング、ミルフォードサウンドの静けさ。南緯46度の秋、草原に寝転んで空を見上げ、「自分はいったい何をやっているんだろう」と笑った瞬間を、今もよく覚えています。',
    ],
    description_en: [
      'I want to circle the world under my own power — that was the dream. At the end of my first year of university, the South Island of New Zealand became its first training ground: ferry across the strait from Wellington, then about forty days to Bluff at the island’s southern tip.',
      'Earthquakes and wildfires had closed the highways, so I detoured onto the unpaved Rainbow Road: 120 kilometers at 8 km/h, where an approaching car announces itself with a dust cloud a full hour before you pass it. I drank from the same streams as the cattle, and in an empty backcountry hut I played music at full volume by candlelight, out-shouting the loneliness until morning.',
      'The stars over Lake Tekapo, a trek at Mt Cook, the hush of Milford Sound. Autumn at 46 degrees south, lying back in the grass, laughing at the sky: what on earth am I doing? I remember that moment still.',
    ],
    tags: [
      { ja: '自転車', en: 'Bicycle', cat: 'vehicle' },
      { ja: 'テント泊', en: 'Camping', cat: 'stay' },
      { ja: '海外', en: 'Overseas', cat: 'region' },
    ],
    route: 'nz-cycling',
    routeKind: 'approx',
    datesFromLog: false,
    cities: [
      { ja: 'ウェリントン', en: 'Wellington', lon: 174.78, lat: -41.29, labelDy: -8 },
      { ja: 'ピクトン', en: 'Picton', lon: 174.0, lat: -41.29, labelDy: -28 },
      { ja: 'ブレナム', en: 'Blenheim', lon: 173.96, lat: -41.51, labelDy: 10 },
      { ja: 'レインボーロード', en: 'Rainbow Road', lon: 172.75, lat: -42.1 },
      { ja: 'ハンマースプリングス', en: 'Hanmer Springs', lon: 172.83, lat: -42.52, labelFlip: true },
      { ja: 'カイコウラ', en: 'Kaikoura', lon: 173.68, lat: -42.4 },
      { ja: 'クライストチャーチ', en: 'Christchurch', lon: 172.64, lat: -43.53 },
      { ja: 'フェアリー', en: 'Fairlie', lon: 170.83, lat: -44.1, labelDy: -6 },
      { ja: 'テカポ湖', en: 'Lake Tekapo', lon: 170.48, lat: -44.0, labelFlip: true, labelDy: 14 },
      { ja: 'マウントクック', en: 'Mt Cook', lon: 170.1, lat: -43.73, kind: 'peak', labelFlip: true },
      { ja: 'クイーンズタウン', en: 'Queenstown', lon: 168.66, lat: -45.03 },
      { ja: 'ミルフォードサウンド', en: 'Milford Sound', lon: 167.93, lat: -44.67 },
      { ja: 'ダウトフルサウンド', en: 'Doubtful Sound', lon: 167.1, lat: -45.32 },
      { ja: 'インバーカーギル', en: 'Invercargill', lon: 168.35, lat: -46.41, labelFlip: true },
      { ja: 'ブラフ', en: 'Bluff', lon: 168.33, lat: -46.6, labelDy: 10 },
      { ja: 'ダニーデン', en: 'Dunedin', lon: 170.5, lat: -45.87 },
      { ja: 'オアマル', en: 'Oamaru', lon: 170.97, lat: -45.1 },
    ],
  },
  {
    id: 'japan-hitchhike',
    sortKey: '2017-11',
    vehicle: 'thumb',
    title_ja: 'ヒッチハイク日本縦断',
    title_en: 'Hitchhiking the Length of Japan',
    period_ja: '2017年11月・約2週間',
    period_en: 'Nov 2017 · ~2 weeks',
    albumUrl: 'https://photos.app.goo.gl/oRgQg76dTyU2cd7JA',
    images: [
      { src: "images/travels/japan-hitchhike/1.jpg", alt_ja: "原爆ドームの前で「ヒッチハイク日本縦断」の看板を手に", alt_en: "Holding my 'Hitchhiking Japan' sign at the Atomic Bomb Dome, Hiroshima" },
      { src: "images/travels/japan-hitchhike/2.jpg", alt_ja: "出発の日、桜島を背に看板を掲げて（鹿児島）", alt_en: "Day one: holding up the sign with Sakurajima behind, Kagoshima" },
      { src: "images/travels/japan-hitchhike/3.jpg", alt_ja: "夜を過ごした、温泉付きのネットカフェ", alt_en: "The internet café with hot baths where I spent the night" },
      { src: "images/travels/japan-hitchhike/4.jpg", alt_ja: "「九州最後のサービスエリア」の横断幕の前で", alt_en: "Under the banner of Kyushu's last service area" },
      { src: "images/travels/japan-hitchhike/5.jpg", alt_ja: "仙台・伊達政宗騎馬像の前で看板を手に", alt_en: "With the sign before the statue of Date Masamune, Sendai" },
      { src: "images/travels/japan-hitchhike/6.jpg", alt_ja: "乗せてくれた方と、看板を手に記念の自撮り", alt_en: "A thank-you selfie with a driver who gave me a lift" },
      { src: "images/travels/japan-hitchhike/7.jpg", alt_ja: "夜のフェリーターミナル。青函フェリーで北海道へ", alt_en: "The ferry terminal at night, bound for Hokkaido" },
      { src: "images/travels/japan-hitchhike/8.jpg", alt_ja: "函館駅の前で看板を手に", alt_en: "With the sign in front of Hakodate Station" },
      { src: "images/travels/japan-hitchhike/9.jpg", alt_ja: "北海道で出会った方と、看板を手に自撮り", alt_en: "A selfie with someone I met in Hokkaido" },
      { src: "images/travels/japan-hitchhike/10.jpg", alt_ja: "ゴールの札幌・羊ヶ丘のクラーク像の前で", alt_en: "At the goal: the Clark statue on Hitsujigaoka, Sapporo" },
    ],
    description_ja: [
      '「課題さえ出せば、出席は自由」。大学2年の秋学期にそんな2週間の空白を見つけて、鹿児島行きの飛行機に乗りました。帰りの手段は、親指ひとつ。桜島を背にスケッチブックの看板を掲げ、札幌までの北上が始まります。',
      '乗せてくださったのは、およそ50台。営業車のお兄さん、長距離トラックの運転手さん、家族連れ。車内では、乗せてくれた人の数だけ人生の話を聞きました。夜は快活CLUBに泊まり、備え付けのパソコンで大学のレポートを書いて提出する日々です。',
      'ゴールの札幌では、クラーク像と同じポーズで記念撮影。見上げた北の空には、その年最初の雪がちらついていました。',
    ],
    description_en: [
      'Attendance optional, as long as the assignments arrive. Finding a two-week gap in my second-year autumn term, I flew to Kagoshima with only a thumb for the return ticket. With Sakurajima smoking behind me, I held up a sketchbook sign, and the long ride north to Sapporo began.',
      'Roughly fifty cars picked me up — sales reps, long-haul truckers, families — and every ride came with a life story. Nights were spent in internet cafés, writing university reports on their PCs and clicking submit.',
      'At the goal in Sapporo I posed like the Clark statue: Boys, be ambitious. In the northern sky above, the first snow of the year was starting to fall.',
    ],
    tags: [
      { ja: 'ヒッチハイク', en: 'Hitchhiking', cat: 'vehicle' },
      { ja: '国内', en: 'Japan', cat: 'region' },
    ],
    route: 'japan-hitchhike',
    routeKind: 'recorded',
    datesFromLog: true,
    cities: [
      { ja: '鹿児島', en: 'Kagoshima', lon: 130.56, lat: 31.6 },
      { ja: '広島', en: 'Hiroshima', lon: 132.46, lat: 34.4 },
      { ja: '奈良（実家）', en: 'Nara (home)', lon: 135.7, lat: 34.55 },
      { ja: '東京', en: 'Tokyo', lon: 139.69, lat: 35.68 },
      { ja: '仙台', en: 'Sendai', lon: 140.87, lat: 38.27 },
      { ja: '青森', en: 'Aomori', lon: 140.74, lat: 40.82 },
      { ja: '函館', en: 'Hakodate', lon: 140.73, lat: 41.77 },
      { ja: '札幌', en: 'Sapporo', lon: 141.35, lat: 43.06 },
    ],
  },
  {
    id: 'vietnam-motorbike',
    sortKey: '2018-09',
    vehicle: 'motorbike',
    title_ja: 'ベトナム バイク縦断',
    title_en: 'Vietnam by Motorbike',
    period_ja: '2018年9月・約10日',
    period_en: 'Sep 2018 · ~10 days',
    albumUrl: 'https://photos.app.goo.gl/KjkK35jBhqFQHJ3g6',
    images: [
      { src: "images/travels/vietnam-motorbike/1.jpg", alt_ja: "海の向こうに高層ビル群を望む、海沿いの峠道をバイクで", alt_en: "Riding a coastal mountain road, a city skyline across the water" },
      { src: "images/travels/vietnam-motorbike/2.jpg", alt_ja: "ハノイのバイク店で、買った中古バイクと売り手のおじさんと", alt_en: "With the seller and my secondhand motorbike at a Hanoi bike shop" },
      { src: "images/travels/vietnam-motorbike/3.jpg", alt_ja: "バイクと人が行き交うハノイ旧市街の通り", alt_en: "Motorbikes and people crowding a street in Hanoi's Old Quarter" },
      { src: "images/travels/vietnam-motorbike/4.jpg", alt_ja: "路上の修理屋さんでバイクを直してもらう", alt_en: "A roadside mechanic fixing my motorbike" },
      { src: "images/travels/vietnam-motorbike/5.jpg", alt_ja: "石灰岩の峰々に囲まれた、水上に建つ東屋（ニンビン・チャンアン）", alt_en: "A pavilion on the water ringed by limestone peaks, Trang An, Ninh Binh" },
      { src: "images/travels/vietnam-motorbike/6.jpg", alt_ja: "泊めてもらった家族と、笑顔の自撮り", alt_en: "A smiling selfie with the family who put me up" },
      { src: "images/travels/vietnam-motorbike/7.jpg", alt_ja: "青空の下にそびえるフエ王宮の午門", alt_en: "The Noon Gate of Hue's Imperial City under a blue sky" },
      { src: "images/travels/vietnam-motorbike/8.jpg", alt_ja: "色とりどりのランタンが灯るホイアンの夜", alt_en: "Hoi An at night, glowing with colorful lanterns" },
      { src: "images/travels/vietnam-motorbike/9.jpg", alt_ja: "旅先で出会った人たちと、海辺で自撮り", alt_en: "A seaside selfie with people I met on the road" },
      { src: "images/travels/vietnam-motorbike/10.jpg", alt_ja: "お世話になった家庭の台所で、夕食の支度", alt_en: "Getting dinner ready in the kitchen of a family who hosted me" },
    ],
    description_ja: [
      'ハノイで手に入れたのは、走行8万kmの年季の入った中古バイク。国道1号線を南へ、約10日かけてホーチミンを目指しました。スコールも、市場の朝も、ぜんぶ道の上です。',
      '小さな故障をだましだまし直しながらの道中、ニャチャンの先の山道でエンジンが完全に沈黙しました。惰性で転がり込んだ先は、奇跡のように、村のバイク屋さんの目の前。交換エンジンを待つ2日間は、お店の青年の家にホームステイとなりました。庭の鶏を一羽つぶしてもてなしてくださり、夕方はみんなで浜へ出てカニを獲る。その晩ごはんの味は、今も忘れられません。',
      '人の温かさに助けられ通しの縦断でした。ゴールのホーチミンで、相棒は500円で次の持ち主のもとへ。良い旅を、と見送りました。',
    ],
    description_en: [
      'In Hanoi I bought a seasoned machine with 80,000 kilometers on the clock, and pointed it down Highway 1 — ten days south to Ho Chi Minh City, with every squall and every morning market happening right there on the road.',
      'I nursed it through small breakdowns until, on a climb past Nha Trang, the engine went completely silent — and the bike coasted to a stop, miraculously, in front of a village mechanic. While a replacement engine took two days to arrive, I homestayed with the young mechanic’s family: a backyard chicken cooked in my honor, evenings catching crabs on the beach for dinner. I can still taste it.',
      'It was a traverse carried the whole way by human kindness. In Ho Chi Minh City my companion went off to its next owner for the price of a coffee. Safe travels, partner.',
    ],
    tags: [
      { ja: 'バイク', en: 'Motorbike', cat: 'vehicle' },
      { ja: '海外', en: 'Overseas', cat: 'region' },
    ],
    route: 'vietnam-motorbike',
    routeKind: 'recorded',
    datesFromLog: true,
    cities: [
      { ja: 'ハノイ', en: 'Hanoi', lon: 105.85, lat: 21.03 },
      { ja: 'ニンビン', en: 'Ninh Binh', lon: 105.97, lat: 20.25 },
      { ja: 'フエ', en: 'Hue', lon: 107.58, lat: 16.46 },
      { ja: 'ダナン', en: 'Da Nang', lon: 108.2, lat: 16.05, labelDy: -8 },
      { ja: 'ホイアン', en: 'Hoi An', lon: 108.33, lat: 15.88, labelDy: 10 },
      { ja: 'ニャチャン', en: 'Nha Trang', lon: 109.19, lat: 12.24 },
      { ja: 'ホーチミン', en: 'Ho Chi Minh City', lon: 106.7, lat: 10.78 },
    ],
  },
  {
    id: 'srilanka-tuktuk',
    sortKey: '2019-01',
    vehicle: 'tuktuk',
    title_ja: 'スリランカ一周 トゥクトゥク旅',
    title_en: 'Around Sri Lanka by Tuk-tuk',
    period_ja: '2019年1月〜2月',
    period_en: 'Jan–Feb 2019',
    albumUrl: 'https://photos.app.goo.gl/VLTFZq5t5zY7uFYj7',
    images: [
      { src: "images/travels/srilanka-tuktuk/1.jpg", alt_ja: "茶畑の中の道で、赤いトゥクトゥクと並んで", alt_en: "Standing beside the red tuk-tuk on a lane through the tea fields" },
      { src: "images/travels/srilanka-tuktuk/2.jpg", alt_ja: "ゾウ注意の標識の下に停めた赤いトゥクトゥク", alt_en: "The red tuk-tuk parked under an elephant-crossing sign" },
      { src: "images/travels/srilanka-tuktuk/3.jpg", alt_ja: "地元の床屋で散髪してもらいながら、鏡越しに自撮り", alt_en: "A mirror selfie mid-haircut at a local barbershop" },
      { src: "images/travels/srilanka-tuktuk/4.jpg", alt_ja: "木陰でくつろぐラングールの群れ（アヌラーダプラ）", alt_en: "A troop of langurs lounging in the shade at Anuradhapura" },
      { src: "images/travels/srilanka-tuktuk/5.jpg", alt_ja: "海の上をまっすぐ延びる道で、旅先で出会った仲間たちと", alt_en: "With friends from the road on a causeway running straight across the sea" },
      { src: "images/travels/srilanka-tuktuk/6.jpg", alt_ja: "緑の森からそびえ立つシギリヤ・ロック", alt_en: "Sigiriya Rock rising from the green forest" },
      { src: "images/travels/srilanka-tuktuk/7.jpg", alt_ja: "森に囲まれたナインアーチブリッジ（エッラ）", alt_en: "The Nine Arch Bridge in Ella, wrapped in forest" },
      { src: "images/travels/srilanka-tuktuk/8.jpg", alt_ja: "スリランカ国旗が揺れるゴールの街並み", alt_en: "Sri Lankan flags fluttering over a street in Galle" },
      { src: "images/travels/srilanka-tuktuk/9.jpg", alt_ja: "夕日に染まる浜辺と伝統的な帆掛け船", alt_en: "A traditional outrigger sailboat on the beach at sunset" },
      { src: "images/travels/srilanka-tuktuk/10.jpg", alt_ja: "宿で出会った旅人たちと囲んだ、にぎやかな食卓", alt_en: "A lively table with fellow travelers at the guesthouse" },
    ],
    description_ja: [
      '就活で気分が沈んでいた、大学3年の冬のこと。「旅してるときが一番輝いてるのに」——先輩の一言と飲み会の勢いで、その夜のうちにスリランカ行きの航空券を予約していました。',
      '現地で借りた相棒は、日の丸ステッカーを貼った赤いトゥクトゥク。「こんなに長く借りたお客は初めてだ」とレンタル屋さんは目を丸くします。「夜は象が出るから走るな」という忠告を冗談だと笑っていたら、ある晩、闇の中で道を塞ぐ黒い壁が本物の象でした。急ブレーキとフルステアで、間一髪。しばらく象がトラウマになりました。',
      '道端ではタクシーと間違われて通行人を乗せ、お駄賃に20円をいただいたことも。古都アヌラーダプラから北端のジャフナ、紅茶の高原ヌワラエリヤ、城塞都市ゴールまで。三輪の相棒と、島をまるごと一周しました。',
    ],
    description_en: [
      'Third-year winter, spirits flattened by job hunting. “You shine brightest when you travel,” a senior told me over drinks — and by the end of that night I had booked a flight to Sri Lanka.',
      'My companion there was a red tuk-tuk wearing a Japanese-flag sticker. The rental shop was amazed anyone would keep one so long. “Don’t drive at night — elephants,” they warned; I laughed it off as a joke, until one night a black wall across the dark road turned out to be exactly that. Full brakes, full steering, missed by a whisker. Elephants haunted me for a while.',
      'Locals kept flagging me down as a taxi; once I earned a twenty-yen fare. From the ancient capital Anuradhapura to Jaffna at the northern tip, the tea highlands of Nuwara Eliya, the fort town of Galle — three wheels, one whole island.',
    ],
    tags: [
      { ja: 'トゥクトゥク', en: 'Tuk-tuk', cat: 'vehicle' },
      { ja: '海外', en: 'Overseas', cat: 'region' },
    ],
    route: 'srilanka-tuktuk',
    routeKind: 'recorded',
    datesFromLog: true,
    cities: [
      { ja: 'ニゴンボ', en: 'Negombo', lon: 79.84, lat: 7.21 },
      { ja: 'コロンボ', en: 'Colombo', lon: 79.86, lat: 6.93 },
      { ja: 'アヌラーダプラ', en: 'Anuradhapura', lon: 80.4, lat: 8.31 },
      { ja: 'ジャフナ', en: 'Jaffna', lon: 80.02, lat: 9.66 },
      { ja: 'トリンコマリー', en: 'Trincomalee', lon: 81.23, lat: 8.57 },
      { ja: 'シギリヤ', en: 'Sigiriya', lon: 80.76, lat: 7.96, labelDy: -6 },
      { ja: 'ダンブッラ', en: 'Dambulla', lon: 80.65, lat: 7.87, labelFlip: true },
      { ja: 'キャンディ', en: 'Kandy', lon: 80.64, lat: 7.29 },
      { ja: 'ヌワラエリヤ', en: 'Nuwara Eliya', lon: 80.77, lat: 6.97 },
      { ja: 'ゴール', en: 'Galle', lon: 80.22, lat: 6.03 },
    ],
  },
  {
    id: 'no-fly-turkey',
    sortKey: '2019-08',
    vehicle: 'train',
    title_ja: '飛ばずにイスタンブール——飛行機を使わずトルコへ',
    title_en: 'To Istanbul Without Flying',
    period_ja: '2019年夏・約25日',
    period_en: 'Summer 2019 · ~25 days',
    albumUrl: 'https://photos.app.goo.gl/1ZLhR58zURyki5ex7',
    images: [
      { src: "images/travels/no-fly-turkey/1.jpg", alt_ja: "パミールの雪山を望むキルギスの高原と牛たち", alt_en: "Cattle grazing on a Kyrgyz highland beneath the snowy Pamirs" },
      { src: "images/travels/no-fly-turkey/2.jpg", alt_ja: "大阪から上海へ向かうフェリーの上で迎えた朝日", alt_en: "Sunrise from the ferry between Osaka and Shanghai" },
      { src: "images/travels/no-fly-turkey/3.jpg", alt_ja: "カシュガル旧市街を行き交う人々", alt_en: "Families strolling through Kashgar's old town" },
      { src: "images/travels/no-fly-turkey/4.jpg", alt_ja: "キルギスへ向かう乗り合いのバンと同乗者たち", alt_en: "Our shared van and fellow passengers on the road into Kyrgyzstan" },
      { src: "images/travels/no-fly-turkey/5.jpg", alt_ja: "ビシュケク中心部、キルギス国旗がはためく建物と花壇", alt_en: "Flower beds and the Kyrgyz flag in central Bishkek" },
      { src: "images/travels/no-fly-turkey/6.jpg", alt_ja: "カザフスタンの駅で長距離列車に乗り込む人々", alt_en: "Passengers boarding a long-distance train in Kazakhstan" },
      { src: "images/travels/no-fly-turkey/7.jpg", alt_ja: "ドンブラ奏者を描いた、カザフスタン西部のフィルハーモニーの壁画", alt_en: "A mural of a dombra player on a philharmonic hall in western Kazakhstan" },
      { src: "images/travels/no-fly-turkey/8.jpg", alt_ja: "深夜、カスピ海を渡る船に乗り込む", alt_en: "Boarding the ship across the Caspian Sea in the middle of the night" },
      { src: "images/travels/no-fly-turkey/9.jpg", alt_ja: "夕暮れのバクー、フレイム・タワーズと海沿いの夜景", alt_en: "Baku's Flame Towers and the seafront at dusk" },
      { src: "images/travels/no-fly-turkey/10.jpg", alt_ja: "燃えるような夕焼けに浮かぶ、イスタンブールのモスクのシルエット", alt_en: "Istanbul's mosque silhouettes against a blazing sunset" },
    ],
    description_ja: [
      'お風呂に浸かっていて、ふと思いました。「飛行機を使わずに、どこまで遠くへ行けるんだろう」。調べてみると、上海行きの船があり、その先は鉄路がユーラシアの果てまで続いています。行くしかありません。',
      '大阪から48時間のフェリーで上海へ（船内カラオケで日中歌合戦になりました）。鉄道で西安、カシュガルへ。標高数千mのイルケシュタム峠を乗合バンで越えるとき、「夜は狼が出るから気をつけろ」と言われた国境の向こうに、天山山脈の雪嶺が平原から立ち上がっていました。息を呑む、とはあのことです。',
      'カザフスタンのアクタウからは週1便の貨物船でカスピ海を渡り、バクー、トビリシ、黒海沿いの国境の街バトゥミへ。最後は歩いて国境を越え、夜行列車でイスタンブールに滑り込みました。25日間のうち16日は、何かに乗っていた計算になります。バックパックの中身は院試の教科書——電波の届かない船と列車は、世界一集中できる勉強部屋でした。おかげさまで、合格です。',
    ],
    description_en: [
      'It came to me in the bath: how far can a person get without an airplane? A little research found a ferry to Shanghai, and beyond it, rails running to the far end of Eurasia. There was nothing to do but go.',
      'Forty-eight hours by ferry from Osaka to Shanghai (the onboard karaoke turned into a Japan-China song battle). Trains to Xi’an, then Kashgar. Crossing the several-thousand-meter Irkeshtam Pass in a shared van — “careful, wolves at night,” they said at the border — the snowbound Tian Shan rose straight out of the steppe on the far side. Breathtaking is the only word.',
      'From Aktau in Kazakhstan, a once-a-week cargo ship across the Caspian; then Baku, Tbilisi, and Batumi, the border town on the Black Sea. I walked across the border into Turkey and slid into Istanbul on a night train. Sixteen of the twenty-five days were spent aboard something. My backpack was full of grad-school exam textbooks — ships and trains beyond signal range make the world’s finest study room. I passed, by the way.',
    ],
    tags: [
      { ja: '鉄道・船', en: 'Rail & sea', cat: 'vehicle' },
      { ja: '海外', en: 'Overseas', cat: 'region' },
      { ja: 'ユーラシア横断', en: 'Across Eurasia', cat: 'theme' },
    ],
    route: 'no-fly-turkey',
    routeKind: 'approx',
    datesFromLog: false,
    cities: [
      { ja: '大阪', en: 'Osaka', lon: 135.5, lat: 34.69 },
      { ja: '上海', en: 'Shanghai', lon: 121.47, lat: 31.23 },
      { ja: '西安', en: "Xi'an", lon: 108.94, lat: 34.34 },
      { ja: 'カシュガル', en: 'Kashgar', lon: 75.99, lat: 39.47, labelFlip: true, labelDx: -20, labelDy: 6 },
      { ja: 'オシュ', en: 'Osh', lon: 72.8, lat: 40.53, labelDy: 5, labelDx: 20 },
      { ja: 'ビシュケク', en: 'Bishkek', lon: 74.59, lat: 42.87, labelDy: -10 },
      { ja: 'シムケント', en: 'Shymkent', lon: 69.59, lat: 42.32, labelFlip: true, labelDy: 10 },
      { ja: 'アクタウ', en: 'Aktau', lon: 51.16, lat: 43.65, labelDy: -14 },
      { ja: 'バクー', en: 'Baku', lon: 49.87, lat: 40.41, labelDy: 20 },
      { ja: 'トビリシ', en: 'Tbilisi', lon: 44.78, lat: 41.72, labelDy: -23, labelDx: -12 },
      { ja: 'バトゥミ', en: 'Batumi', lon: 41.64, lat: 41.65, labelFlip: true, labelDy: -23 },
      { ja: 'カルス', en: 'Kars', lon: 43.09, lat: 40.6, labelDy: 16, labelDx: -8 },
      { ja: 'アンカラ', en: 'Ankara', lon: 32.86, lat: 39.93, labelDy: 18 },
      { ja: 'イスタンブール', en: 'Istanbul', lon: 28.98, lat: 41.01, labelDx: -21, labelDy: -4 },
    ],
  },
  {
    id: 'hokkaido-cub',
    sortKey: '2020-06',
    vehicle: 'cub',
    title_ja: 'スーパーカブで北海道一周',
    title_en: 'Around Hokkaido on a Super Cub',
    period_ja: '2020年6月〜8月・約1ヶ月半',
    period_en: 'Jun–Aug 2020 · ~6 weeks',
    albumUrl: 'https://photos.app.goo.gl/2V8EZwhFSug9nMGE8',
    images: [
      { src: "images/travels/hokkaido-cub/1.jpg", alt_ja: "オトンルイ風力発電所の風車が並ぶ海沿いの道とスーパーカブ（幌延）", alt_en: "The Super Cub on the coast road lined with the Otonrui wind turbines, Horonobe" },
      { src: "images/travels/hokkaido-cub/2.jpg", alt_ja: "北海道へ渡るフェリーの乗船口", alt_en: "Trucks boarding the ferry bound for Hokkaido" },
      { src: "images/travels/hokkaido-cub/3.jpg", alt_ja: "雲海の上にそびえる利尻山の山頂", alt_en: "The summit of Mt. Rishiri rising above a sea of clouds" },
      { src: "images/travels/hokkaido-cub/4.jpg", alt_ja: "美瑛の青い池", alt_en: "The Blue Pond in Biei" },
      { src: "images/travels/hokkaido-cub/5.jpg", alt_ja: "雲海を見下ろす山頂でガッツポーズ", alt_en: "Fist raised on a summit above the sea of clouds" },
      { src: "images/travels/hokkaido-cub/6.jpg", alt_ja: "羽化したばかりのセミと抜け殻", alt_en: "A cicada freshly emerged beside its shell" },
      { src: "images/travels/hokkaido-cub/7.jpg", alt_ja: "民芸品店でアイヌの民族衣装を着せてもらって", alt_en: "Trying on traditional Ainu dress at a craft shop" },
      { src: "images/travels/hokkaido-cub/8.jpg", alt_ja: "夕焼け空を背に、丘の上に立つエゾシカのシルエット", alt_en: "An Ezo deer silhouetted on a ridge against the sunset sky" },
      { src: "images/travels/hokkaido-cub/9.jpg", alt_ja: "緑の湿原にたたずむタンチョウ", alt_en: "A red-crowned crane standing in a green marsh" },
      { src: "images/travels/hokkaido-cub/10.jpg", alt_ja: "木箱が天井近くまで積み上がった作業小屋", alt_en: "A work shed stacked nearly to the roof with wooden crates" },
    ],
    description_ja: [
      '本当はその夏、ドナウ川をカヤックで下るつもりでした。コロナ禍で国境が閉じ、代わりに選んだ相棒がスーパーカブです。大学院1年の夏、キャンプ道具を満載して、北海道をまるごと一周しました。',
      '海沿いをなぞるだけでは終わりません。旭川や帯広など内陸までくまなく走り、利尻山から羊蹄山まで、道内の日本百名山をすべて登頂しました。平日の日中は、旅先からリモートで大学院の授業に出席し、修士論文を書き進めます。',
      'ワーケーションを研究する大学院生が、自分自身を実験台にした約1ヶ月半。研究テーマと人生が、いちばん近づいた夏でした。',
    ],
    description_en: [
      'That summer was supposed to be a kayak descent of the Danube. The pandemic closed the borders, and a Honda Super Cub became the substitute partner. In the first summer of grad school, loaded with camping gear, I lapped the whole of Hokkaido.',
      'Not just the coastline, either — inland to Asahikawa and Obihiro, climbing every one of Hokkaido’s “100 Famous Mountains,” from Rishiri to Yotei. On weekdays I attended grad-school classes remotely from wherever the road had left me, and kept the master’s thesis moving.',
      'A workation researcher, running the experiment on himself for six weeks. Never before or since have my research topic and my life been so perfectly aligned.',
    ],
    tags: [
      { ja: 'スーパーカブ', en: 'Super Cub', cat: 'vehicle' },
      { ja: 'テント泊', en: 'Camping', cat: 'stay' },
      { ja: '国内', en: 'Japan', cat: 'region' },
      { ja: '登山', en: 'Mountains', cat: 'theme' },
      { ja: 'ワーケーション', en: 'Workation', cat: 'theme' },
    ],
    route: 'hokkaido-cub',
    routeKind: 'recorded',
    datesFromLog: true,
    cities: [
      { ja: '洞爺湖', en: 'Lake Toya', lon: 140.85, lat: 42.55 },
      { ja: '苫小牧', en: 'Tomakomai', lon: 141.6, lat: 42.63 },
      { ja: '札幌', en: 'Sapporo', lon: 141.35, lat: 43.06 },
      { ja: '旭川', en: 'Asahikawa', lon: 142.36, lat: 43.77 },
      { ja: '帯広', en: 'Obihiro', lon: 143.2, lat: 42.92 },
      { ja: '根室', en: 'Nemuro', lon: 145.58, lat: 43.33, labelDy: 12 },
      { ja: '利尻山', en: 'Mt. Rishiri', lon: 141.24, lat: 45.18, kind: 'peak' },
      { ja: '羅臼岳', en: 'Mt. Rausu', lon: 145.12, lat: 44.08, kind: 'peak' },
      { ja: '斜里岳', en: 'Mt. Shari', lon: 144.72, lat: 43.77, kind: 'peak' },
      { ja: '雌阿寒岳', en: 'Mt. Meakan', lon: 144.01, lat: 43.39, kind: 'peak' },
      { ja: '旭岳（大雪山）', en: 'Mt. Asahi (Daisetsuzan)', lon: 142.85, lat: 43.66, kind: 'peak', labelDy: -6 },
      { ja: 'トムラウシ山', en: 'Mt. Tomuraushi', lon: 142.85, lat: 43.53, kind: 'peak' },
      { ja: '十勝岳', en: 'Mt. Tokachi', lon: 142.69, lat: 43.42, kind: 'peak', labelDy: 6 },
      { ja: '幌尻岳', en: 'Mt. Poroshiri', lon: 142.68, lat: 42.72, kind: 'peak' },
      { ja: '羊蹄山', en: 'Mt. Yotei', lon: 140.81, lat: 42.83, kind: 'peak' },
      { ja: '室蘭', en: 'Muroran', lon: 140.97, lat: 42.32 },
      { ja: '白老', en: 'Shiraoi', lon: 141.36, lat: 42.55, labelDy: 10 },
      { ja: '稚内', en: 'Wakkanai', lon: 141.67, lat: 45.42 },
      { ja: '網走', en: 'Abashiri', lon: 144.27, lat: 44.02 },
      { ja: '知床', en: 'Shiretoko', lon: 144.98, lat: 44.05, labelFlip: true },
      { ja: '富良野', en: 'Furano', lon: 142.38, lat: 43.34, labelFlip: true },
      { ja: '釧路', en: 'Kushiro', lon: 144.38, lat: 42.98 },
      { ja: '襟裳岬', en: 'Cape Erimo', lon: 143.25, lat: 41.93 },
      { ja: '納沙布岬', en: 'Cape Nosappu', lon: 145.82, lat: 43.38, labelFlip: true },
    ],
  },
  {
    id: 'kyushu-okinawa-cub',
    sortKey: '2021-02',
    vehicle: 'cub',
    title_ja: 'スーパーカブで九州・沖縄一周',
    title_en: 'Kyushu & Okinawa on a Super Cub',
    period_ja: '2021年2月〜3月',
    period_en: 'Feb–Mar 2021',
    albumUrl: 'https://photos.app.goo.gl/Hn6eYqwUGeaGTMdC8',
    images: [
      { src: "images/travels/kyushu-okinawa-cub/1.jpg", alt_ja: "エメラルドの海を渡る古宇利大橋とスーパーカブ（沖縄）", alt_en: "The Super Cub on the Kouri Bridge over Okinawa's emerald sea" },
      { src: "images/travels/kyushu-okinawa-cub/2.jpg", alt_ja: "吹雪の祖母山山頂（1,756.4m）で笑顔の自撮り", alt_en: "A grinning selfie on the snowy summit of Mt. Sobo (1,756.4 m)" },
      { src: "images/travels/kyushu-okinawa-cub/3.jpg", alt_ja: "雪の残る、祖母・傾・大崩ユネスコエコパークの看板とスーパーカブ", alt_en: "The Super Cub beside the Sobo-Katamuki-Okue UNESCO Eco-Park sign in the snow" },
      { src: "images/travels/kyushu-okinawa-cub/4.jpg", alt_ja: "うっすらと雪をまとった阿蘇の山並み", alt_en: "Mt. Aso's ridges dusted with snow" },
      { src: "images/travels/kyushu-okinawa-cub/5.jpg", alt_ja: "屋久島の森にそびえる、苔むした屋久杉", alt_en: "A moss-covered ancient Yakusugi cedar in the forests of Yakushima" },
      { src: "images/travels/kyushu-okinawa-cub/6.jpg", alt_ja: "雪の残る屋久島の奥岳の稜線", alt_en: "Snow-streaked peaks in the heart of Yakushima" },
      { src: "images/travels/kyushu-okinawa-cub/7.jpg", alt_ja: "荷物を満載した旅のスーパーカブ", alt_en: "The Super Cub loaded to the brim with gear" },
      { src: "images/travels/kyushu-okinawa-cub/8.jpg", alt_ja: "線路の先にそびえる開聞岳", alt_en: "Mt. Kaimon rising beyond the railway tracks" },
      { src: "images/travels/kyushu-okinawa-cub/9.jpg", alt_ja: "鹿児島から奄美・沖縄へ向かうフェリーの甲板", alt_en: "On deck of the ferry from Kagoshima to Amami and Okinawa" },
      { src: "images/travels/kyushu-okinawa-cub/10.jpg", alt_ja: "沖縄美ら海水族館の大水槽を泳ぐジンベエザメ", alt_en: "Whale sharks gliding through the giant tank at Okinawa Churaumi Aquarium" },
    ],
    description_ja: [
      '大学院の修了と就職のあいだ、人生の節目の春休みです。スーパーカブで九州をひとまわりして、フェリーで屋久島、そして沖縄へ渡りました。',
      '真冬の九州は、雪の中の登山になります。祖母山、阿蘇、霧島、開聞岳、そして屋久島の宮之浦岳——九州の日本百名山を、雪をかき分けながらすべて登頂しました。',
      '沖縄では、ゲストハウスに1週間の長居です。毎夕、見知らぬ者同士が一品ずつ持ち寄って車座になる「ゆんたく」の輪。那覇から今帰仁まで襷をつなぐ駅伝にも、飛び入りで走らせてもらいました。旅の終わりには、知らない島がひとつ、帰る場所になっていました。',
    ],
    description_en: [
      'The spring break between finishing grad school and starting work — a hinge in life. I looped Kyushu on the Super Cub, then took ferries onward to Yakushima and Okinawa.',
      'Kyushu in midwinter means climbing through snow: Mt. Sobo, Aso, Kirishima, Kaimon, and Yakushima’s Miyanoura-dake — every one of Kyushu’s “100 Famous Mountains,” waded through snowdrifts.',
      'In Okinawa I settled into a guesthouse for a week. Every evening, strangers formed a circle called yuntaku, each bringing a dish to share. I even jumped into a relay race carrying the sash from Naha to Nakijin. By the end of the trip, an unfamiliar island had quietly become a place to come home to.',
    ],
    tags: [
      { ja: 'スーパーカブ', en: 'Super Cub', cat: 'vehicle' },
      { ja: '国内', en: 'Japan', cat: 'region' },
      { ja: '登山', en: 'Mountains', cat: 'theme' },
    ],
    route: 'kyushu-okinawa-cub',
    routeKind: 'recorded',
    datesFromLog: true,
    cities: [
      { ja: '門司', en: 'Moji', lon: 130.96, lat: 33.94 },
      { ja: '竹田', en: 'Taketa', lon: 131.4, lat: 32.97 },
      { ja: '宮崎', en: 'Miyazaki', lon: 131.42, lat: 31.91 },
      { ja: '桜島', en: 'Sakurajima', lon: 130.66, lat: 31.58 },
      { ja: '那覇', en: 'Naha', lon: 127.68, lat: 26.21 },
      { ja: '今帰仁', en: 'Nakijin', lon: 127.98, lat: 26.69 },
      { ja: '九重山', en: 'Mt. Kuju', lon: 131.25, lat: 33.09, kind: 'peak', labelDy: -12 },
      { ja: '祖母山', en: 'Mt. Sobo', lon: 131.35, lat: 32.83, kind: 'peak', labelDy: 14 },
      { ja: '阿蘇山', en: 'Mt. Aso', lon: 131.1, lat: 32.88, kind: 'peak', labelFlip: true, labelDy: -14, labelDx: 4 },
      { ja: '韓国岳（霧島）', en: 'Mt. Karakuni (Kirishima)', lon: 130.86, lat: 31.93, kind: 'peak', labelFlip: true },
      { ja: '開聞岳', en: 'Mt. Kaimon', lon: 130.53, lat: 31.18, kind: 'peak' },
      { ja: '宮之浦岳（屋久島）', en: 'Mt. Miyanoura (Yakushima)', lon: 130.5, lat: 30.34, kind: 'peak' },
      { ja: '福岡', en: 'Fukuoka', lon: 130.4, lat: 33.59 },
      { ja: '佐賀', en: 'Saga', lon: 130.3, lat: 33.26, labelFlip: true },
      { ja: '長崎', en: 'Nagasaki', lon: 129.87, lat: 32.75, labelFlip: true },
      { ja: '熊本', en: 'Kumamoto', lon: 130.71, lat: 32.8, labelFlip: true },
      { ja: '天草', en: 'Amakusa', lon: 130.19, lat: 32.45, labelFlip: true },
    ],
  },
  {
    id: 'south-america',
    sortKey: '2023-05',
    vehicle: 'backpack',
    title_ja: '南米周遊',
    title_en: 'South America Overland',
    period_ja: '2023年5月〜6月',
    period_en: 'May–Jun 2023',
    albumUrl: 'https://photos.app.goo.gl/JtWS6x9KnTNZGjjG6',
    images: [
      { src: "images/travels/south-america/1.jpg", alt_ja: "ワイナピチュを背に、マチュピチュ遺跡で腕を組んで立つ", alt_en: "Arms crossed at Machu Picchu, with Huayna Picchu rising behind" },
      { src: "images/travels/south-america/2.jpg", alt_ja: "ラパスの家並みの上を行くロープウェイ「ミ・テレフェリコ」", alt_en: "Mi Teleférico cable cars gliding over the rooftops of La Paz" },
      { src: "images/travels/south-america/3.jpg", alt_ja: "ラパス近郊、奇岩が連なる渓谷の眺め", alt_en: "A canyon of jagged rock spires outside La Paz" },
      { src: "images/travels/south-america/4.jpg", alt_ja: "標高6,088m、ワイナ・ポトシの山頂で両手を挙げて", alt_en: "Arms raised on the 6,088 m summit of Huayna Potosí" },
      { src: "images/travels/south-america/5.jpg", alt_ja: "ウユニの列車の墓場に眠る、錆びた蒸気機関車", alt_en: "Rusting steam locomotives at the Uyuni train cemetery" },
      { src: "images/travels/south-america/6.jpg", alt_ja: "夕暮れのウユニ塩湖、鏡のような水面に立つ", alt_en: "Standing on the mirror of the Uyuni salt flats at dusk" },
      { src: "images/travels/south-america/7.jpg", alt_ja: "ブエノスアイレス、マティアスの家でアサドのグリルを前に", alt_en: "By the asado grill at Matías's home in Buenos Aires" },
      { src: "images/travels/south-america/8.jpg", alt_ja: "虹がかかるイグアスの滝", alt_en: "A rainbow arching over the Iguazú Falls" },
      { src: "images/travels/south-america/9.jpg", alt_ja: "イタイプ・ダムの見学ツアーで、ヘルメット姿の自撮り", alt_en: "A hard-hat selfie on the Itaipú Dam tour" },
      { src: "images/travels/south-america/10.jpg", alt_ja: "チチカカ湖に浮かぶ太陽の島（イスラ・デル・ソル）の案内図", alt_en: "A painted map of Isla del Sol on Lake Titicaca" },
    ],
    description_ja: [
      'サイボウズを退職してから、カナダ留学が始まるまで。人生の幕間のような時間を、南米で過ごしました。起点は標高3,600mのラパス。ここで挑んだのが、標高6,088mのワイナ・ポトシです。薄い空気に一歩ごとに息が上がり、人生でいちばんきつい登りでしたが、山頂に立ったときの気分は最高でした。あの景色は、一生忘れません。',
      'ウユニ塩湖の鏡の上に立ち、アンデスを南へ下ってアルゼンチンへ。ブエノスアイレスでは、東京のシェアハウスで一緒に暮らしていた友人のマティアスと再会しました。彼の家に招いてもらい、ご家族みんなで囲んだアサドの、なんと楽しかったこと。イグアスの滝で轟音の水煙を浴びたあとは、パラグアイ経由の陸路でボリビアに舞い戻り、アマゾンの入り口トリニダードへ。ここではバイクを借りて、近くの村々を巡りました。',
      '締めくくりは、クスコから列車で向かったマチュピチュです。雲を頂くワイナピチュを見上げながら、次の章——エンジニアとしての修行の日々——への覚悟を固めました。',
    ],
    description_en: [
      'Between leaving Cybozu and starting school in Canada — an intermission in life, spent in South America. The starting point was La Paz, 3,600 meters up, and the challenge there was Huayna Potosí, 6,088 meters high. Every step in the thin air left me gasping; it was the hardest climb of my life, and standing on the summit was the best feeling I have ever had. I will never forget that view.',
      'I stood on the mirror of the Uyuni salt flats and dropped south along the Andes into Argentina. In Buenos Aires I met up again with Matías, a friend from my share house in Tokyo, who invited me home for an asado with his whole family — such a happy afternoon. After taking the thundering spray of the Iguazú Falls full in the face, I doubled back overland through Paraguay into Bolivia and north to Trinidad, the gateway to the Amazon, where I rented a motorbike and rode out to the villages nearby.',
      'The finale: the train from Cusco to Machu Picchu. Looking up at cloud-capped Huayna Picchu, I steeled myself for the next chapter — the disciplined years of becoming an engineer.',
    ],
    tags: [
      { ja: 'バックパック', en: 'Backpacking', cat: 'vehicle' },
      { ja: '海外', en: 'Overseas', cat: 'region' },
    ],
    route: 'south-america',
    routeKind: 'recorded',
    datesFromLog: true,
    cities: [
      { ja: 'ラパス', en: 'La Paz', lon: -68.15, lat: -16.5, labelDy: 10 },
      { ja: 'ウユニ', en: 'Uyuni', lon: -66.83, lat: -20.46, labelDy: 4 },
      { ja: 'サルタ', en: 'Salta', lon: -65.41, lat: -24.78 },
      { ja: 'ブエノスアイレス', en: 'Buenos Aires', lon: -58.38, lat: -34.6 },
      { ja: 'イグアスの滝', en: 'Iguazú Falls', lon: -54.44, lat: -25.69, labelDy: 12 },
      { ja: 'サンタクルス', en: 'Santa Cruz', lon: -63.18, lat: -17.78 },
      { ja: 'トリニダード', en: 'Trinidad', lon: -64.9, lat: -14.83 },
      { ja: 'クスコ', en: 'Cusco', lon: -71.97, lat: -13.53 },
      { ja: 'マチュピチュ', en: 'Machu Picchu', lon: -72.54, lat: -13.16, kind: 'peak', labelFlip: true },
      { ja: 'サンペドロ・デ・アタカマ', en: 'San Pedro de Atacama', lon: -68.2, lat: -22.91, labelFlip: true },
      { ja: 'シウダー・デル・エステ', en: 'Ciudad del Este', lon: -54.61, lat: -25.51, labelFlip: true, labelDy: 20 },
      { ja: 'アスンシオン', en: 'Asunción', lon: -57.63, lat: -25.28, labelDy: -16 },
      { ja: 'ポトシ', en: 'Potosí', lon: -65.75, lat: -19.58 },
      { ja: 'サン・イグナシオ・デ・モクソス', en: 'San Ignacio de Moxos', lon: -65.63, lat: -14.99, labelDy: 22 },
      { ja: 'ワイナポトシ', en: 'Huayna Potosí', lon: -68.15, lat: -16.26, kind: 'peak', labelDy: -6, labelDx: -22 },
      { ja: 'チチカカ湖', en: 'Lake Titicaca', lon: -69.35, lat: -15.85, labelFlip: true, labelDy: -26, labelDx: -12 },
      { ja: 'コパカバーナ', en: 'Copacabana', lon: -69.09, lat: -16.17, labelFlip: true, labelDy: 24, labelDx: -8 },
      { ja: 'イソラ・デル・ソル', en: 'Isla del Sol', lon: -69.15, lat: -16.02, labelDy: -33, labelDx: -32 },
      { ja: 'プーノ', en: 'Puno', lon: -70.03, lat: -15.84, labelFlip: true },
      { ja: 'リマ', en: 'Lima', lon: -77.03, lat: -12.05 },
    ],
  },
  {
    id: 'us-west-coast',
    sortKey: '2023-12',
    vehicle: 'campervan',
    title_ja: 'アメリカ西海岸 キャンピングカー縦断',
    title_en: 'US West Coast by Campervan',
    period_ja: '2023年12月〜2024年1月',
    period_en: 'Dec 2023 – Jan 2024',
    albumUrl: 'https://photos.app.goo.gl/sYoECPSyZxnm1VST6',
    images: [
      { src: "images/travels/us-west-coast/1.jpg", alt_ja: "キャンピングカーの前で、妻とふたりで自撮り", alt_en: "A selfie with my wife in front of our camper van" },
      { src: "images/travels/us-west-coast/2.jpg", alt_ja: "提灯が灯る夜のリトルトーキョーとロサンゼルス市庁舎", alt_en: "Lanterns glowing in Little Tokyo at night, Los Angeles City Hall behind" },
      { src: "images/travels/us-west-coast/3.jpg", alt_ja: "ドジャースの公式ショップで大谷翔平選手のユニフォームを手に", alt_en: "Holding up a Shohei Ohtani jersey at the Dodgers store" },
      { src: "images/travels/us-west-coast/4.jpg", alt_ja: "デスバレー、ザブリスキーポイントの荒々しい地層", alt_en: "The rugged badlands of Zabriskie Point, Death Valley" },
      { src: "images/travels/us-west-coast/5.jpg", alt_ja: "セコイア国立公園、見上げるほど巨大なジャイアントセコイアの根元で", alt_en: "At the foot of a towering giant sequoia in Sequoia National Park" },
      { src: "images/travels/us-west-coast/6.jpg", alt_ja: "ヨセミテ渓谷にそびえるエル・キャピタン", alt_en: "El Capitan rising over Yosemite Valley" },
      { src: "images/travels/us-west-coast/7.jpg", alt_ja: "サンフランシスコ、フィッシャーマンズワーフの看板と観覧車", alt_en: "The Fisherman's Wharf sign and Ferris wheel in San Francisco" },
      { src: "images/travels/us-west-coast/8.jpg", alt_ja: "Googleのキャンパスで、カラフルな椅子を背にジャンプ", alt_en: "Jumping for joy at the Google campus" },
      { src: "images/travels/us-west-coast/9.jpg", alt_ja: "ポートランドのスチール・ブリッジとウィラメット川", alt_en: "The Steel Bridge over the Willamette River in Portland" },
      { src: "images/travels/us-west-coast/10.jpg", alt_ja: "シアトルのパイク・プレイス・マーケットの前で、妻とふたりで", alt_en: "With my wife in front of Pike Place Market in Seattle" },
    ],
    description_ja: [
      'カナダ留学中の年末年始、当時彼女だった妻とふたりの旅です。ロサンゼルスで合流して、キャンピングカーのハンドルを北へ向けました。',
      'デスバレーの荒野、セコイアとヨセミテの巨木と岩壁、サンフランシスコの坂道、ポートランドの雨。寝床ごと移動する旅は、毎晩ちがう景色を窓の外に連れてきます。',
      'シアトルでキャンピングカーを降り、最後は列車でバンクーバーへ。アメリカ大陸の広さを、ハンドルの重さと窓辺の景色で覚えている旅です。',
    ],
    description_en: [
      'New Year’s holidays during my study in Canada, traveling as a pair with my wife — girlfriend, back then. We met up in Los Angeles and pointed a campervan north.',
      'The wastes of Death Valley, the giant trees and granite of Sequoia and Yosemite, San Francisco’s hills, Portland’s rain. When your bed travels with you, every night parks a different view outside the window.',
      'We returned the van in Seattle and finished by train to Vancouver. The scale of the American continent, remembered through the weight of a steering wheel and a string of windows.',
    ],
    tags: [
      { ja: 'キャンピングカー', en: 'Campervan', cat: 'vehicle' },
      { ja: '海外', en: 'Overseas', cat: 'region' },
      { ja: 'ふたり旅', en: 'Two of us', cat: 'theme' },
    ],
    route: 'us-west-coast',
    routeKind: 'recorded',
    datesFromLog: true,
    cities: [
      { ja: 'ロサンゼルス', en: 'Los Angeles', lon: -118.24, lat: 34.05 },
      { ja: 'デスバレー', en: 'Death Valley', lon: -116.87, lat: 36.46 },
      { ja: 'セコイア国立公園', en: 'Sequoia NP', lon: -118.57, lat: 36.49, kind: 'peak', labelFlip: true },
      { ja: 'ヨセミテ国立公園', en: 'Yosemite NP', lon: -119.54, lat: 37.87, kind: 'peak' },
      { ja: 'サンフランシスコ', en: 'San Francisco', lon: -122.42, lat: 37.77, labelFlip: true },
      { ja: 'ポートランド', en: 'Portland', lon: -122.68, lat: 45.52 },
      { ja: 'シアトル', en: 'Seattle', lon: -122.33, lat: 47.61 },
      { ja: 'バンクーバー', en: 'Vancouver', lon: -123.12, lat: 49.28 },
    ],
  },
  {
    // Reconstructed from booking e-mails and photo geotags (no GPS log):
    // KL 7/5 → train via Gemas to JB 7/11 → Singapore 7/12 → ferry to Batam
    // 7/15 → PELNI KM Kelud to Jakarta 7/17-18 → Whoosh to Bandung 7/22 →
    // train to Yogyakarta 7/27 → night train to Banyuwangi 8/2 → ferry to
    // Gilimanuk and bus to Canggu 8/3.
    id: 'no-fly-bali',
    sortKey: '2024-07',
    vehicle: 'train',
    title_ja: 'マレーシア〜シンガポール〜インドネシア旅',
    title_en: 'Malaysia, Singapore & Indonesia',
    period_ja: '2024年7月〜8月・約5週間',
    period_en: 'Jul–Aug 2024 · ~5 weeks',
    albumUrl: 'https://photos.app.goo.gl/x5D3uHv8QN6uwskz8',
    images: [
      { src: "images/travels/no-fly-bali/1.jpg", alt_ja: "夕暮れのチャングーの浜辺で、沈む夕日を眺めて", alt_en: "Watching the sun go down on the beach at Canggu" },
      { src: "images/travels/no-fly-bali/2.jpg", alt_ja: "極彩色の階段が続くバトゥ洞窟（クアラルンプール）", alt_en: "The rainbow stairway into the Batu Caves, Kuala Lumpur" },
      { src: "images/travels/no-fly-bali/3.jpg", alt_ja: "夜のペトロナスツインタワーと噴水", alt_en: "The Petronas Twin Towers and fountains at night" },
      { src: "images/travels/no-fly-bali/4.jpg", alt_ja: "ミンさんと訪ねた、プトラジャヤのピンクモスク", alt_en: "The pink Putra Mosque in Putrajaya, visited with Min" },
      { src: "images/travels/no-fly-bali/5.jpg", alt_ja: "マーライオンの前で、のけぞってポーズ（シンガポール）", alt_en: "Leaning back for a pose in front of the Merlion, Singapore" },
      { src: "images/travels/no-fly-bali/6.jpg", alt_ja: "夜のガーデンズ・バイ・ザ・ベイのスーパーツリー", alt_en: "The Supertrees of Gardens by the Bay lit up at night" },
      { src: "images/travels/no-fly-bali/7.jpg", alt_ja: "30時間の船旅を共にしたペルニの大型客船「ケルド号」（バタム島）", alt_en: "PELNI's KM Kelud, our home for thirty hours at sea, at Batam" },
      { src: "images/travels/no-fly-bali/8.jpg", alt_ja: "ジャカルタ〜バンドンを結ぶ高速鉄道「Whoosh」", alt_en: "Whoosh, the high-speed train between Jakarta and Bandung" },
      { src: "images/travels/no-fly-bali/9.jpg", alt_ja: "ボロブドゥール遺跡の仏塔の前で座禅", alt_en: "Sitting cross-legged among the stupas of Borobudur" },
      { src: "images/travels/no-fly-bali/10.jpg", alt_ja: "バニュワンギ行きの夜行列車に乗り込む（ジョグジャカルタ）", alt_en: "Boarding the night train to Banyuwangi, Yogyakarta" },
    ],
    description_ja: [
      'カナダ留学（BCIT）の夏休み。クアラルンプールからバリ島まで、飛行機を使わずに陸と海だけで下ってみようと思い立ちました。マレーシア、シンガポール、インドネシアの3か国を、列車とバスと船を乗り継いでつないでいく、約5週間のひとり旅です。',
      'クアラルンプールのドミトリーでは、韓国人のミンさんと意気投合して、一緒にプトラジャヤのピンクモスクを訪ねました。列車でマレー半島を下ってシンガポールへ、フェリーでインドネシアのバタム島へ。そこからは国営船会社ペルニの大型客船に乗り込み、30時間かけて海を渡ってジャカルタへ向かいました。デッキから眺めた海の夕暮れは、今もはっきりと覚えています。',
      'ジャワ島では、開業して間もない高速鉄道でバンドンへ、在来線でジョグジャカルタへ。ボロブドゥールに寄り道してから、夜行列車で島の東端バニュワンギへ抜け、朝のフェリーでバリ島に渡りました。最後の1週間は、宿やコワーキングスペースで仕事をしながら、ときどきプールでひと泳ぎ。夕方になると浜辺へ出て、夕日が沈むのをただ眺める——心がほどけていくような日々でした。',
    ],
    description_en: [
      'Summer break from my studies at BCIT in Canada. The idea: get from Kuala Lumpur to Bali without a single flight — trains, buses and boats only, threading Malaysia, Singapore and Indonesia together over about five weeks, solo.',
      'At a dorm in Kuala Lumpur I hit it off with Min, a fellow traveler from Korea, and we visited the pink mosque in Putrajaya together. Trains carried me down the Malay Peninsula to Singapore, a ferry took me to Batam in Indonesia, and from there a big PELNI passenger ship spent thirty hours at sea on the way to Jakarta. The dusk I watched from its deck is still vivid.',
      'On Java: the newly opened high-speed line to Bandung, ordinary trains on to Yogyakarta, a detour to Borobudur, then a night train to Banyuwangi at the island’s eastern tip and a morning ferry across to Bali. The last week was slow: working from the guesthouse or a coworking space, a swim in the pool now and then, and every evening down at the beach to watch the sun go down. Days that quietly put me back together.',
    ],
    tags: [
      { ja: '鉄道・船', en: 'Rail & sea', cat: 'vehicle' },
      { ja: '海外', en: 'Overseas', cat: 'region' },
      { ja: 'ワーケーション', en: 'Workation', cat: 'theme' },
    ],
    route: 'no-fly-bali',
    routeKind: 'approx',
    datesFromLog: false,
    cities: [
      { ja: 'クアラルンプール', en: 'Kuala Lumpur', lon: 101.69, lat: 3.14 },
      { ja: 'ゲマス', en: 'Gemas', lon: 102.61, lat: 2.585 },
      { ja: 'ジョホールバル', en: 'Johor Bahru', lon: 103.76, lat: 1.46, labelFlip: true, labelDy: -12 },
      { ja: 'シンガポール', en: 'Singapore', lon: 103.85, lat: 1.29, labelFlip: true, labelDy: 8, labelDx: -6 },
      { ja: 'バタム島', en: 'Batam', lon: 104.0, lat: 1.13, labelDy: 14, labelDx: 6 },
      { ja: 'ジャカルタ', en: 'Jakarta', lon: 106.85, lat: -6.2 },
      { ja: 'バンドン', en: 'Bandung', lon: 107.6, lat: -6.91 },
      { ja: 'ジョグジャカルタ', en: 'Yogyakarta', lon: 110.36, lat: -7.8 },
      { ja: 'バニュワンギ', en: 'Banyuwangi', lon: 114.37, lat: -8.21, labelDy: -8 },
      { ja: 'チャングー', en: 'Canggu', lon: 115.14, lat: -8.65, labelDy: 16 },
    ],
  },
  {
    // Reconstructed from booking e-mails and photo geotags (no GPS log):
    // Taoyuan 8/9 → Shifen → Jiufen → east coast to Hualien 8/10 → South-Link
    // via Taitung to Kaohsiung 8/11 → Tainan 8/12-14 → Taichung 8/14 →
    // THSR to Taipei 8/15.
    id: 'taiwan-loop',
    sortKey: '2025-08',
    vehicle: 'train',
    title_ja: '台湾一周 鉄道の旅',
    title_en: 'Around Taiwan by Rail',
    period_ja: '2025年8月・8日間',
    period_en: 'Aug 2025 · 8 days',
    albumUrl: 'https://photos.app.goo.gl/RhfufsjfTz5fVQsx7',
    images: [
      { src: "images/travels/taiwan-loop/1.jpg", alt_ja: "十分の線路の上で、願いを書いた天燈を妻とふたりで", alt_en: "Holding up our wish-covered sky lantern on the tracks at Shifen" },
      { src: "images/travels/taiwan-loop/2.jpg", alt_ja: "赤い提灯が連なる夜の九份", alt_en: "Jiufen at night, strung with red lanterns" },
      { src: "images/travels/taiwan-loop/3.jpg", alt_ja: "高雄・美麗島駅のステンドグラス「光之穹頂」", alt_en: "The Dome of Light stained glass at Formosa Boulevard Station, Kaohsiung" },
      { src: "images/travels/taiwan-loop/4.jpg", alt_ja: "無数の灯籠が灯る高雄の廟", alt_en: "A Kaohsiung temple hall glowing with rows of lanterns" },
      { src: "images/travels/taiwan-loop/5.jpg", alt_ja: "蓮池潭の龍虎塔を背に、妻とふたりで自撮り（高雄）", alt_en: "A selfie with my wife before the Dragon and Tiger Pagodas, Kaohsiung" },
      { src: "images/travels/taiwan-loop/6.jpg", alt_ja: "台南の宿で見た、台風の接近を伝えるニュース", alt_en: "TV news of an approaching typhoon, seen from our room in Tainan" },
      { src: "images/travels/taiwan-loop/7.jpg", alt_ja: "ホームに入線する赤白の特急列車", alt_en: "A red-and-white express pulling into the platform" },
      { src: "images/travels/taiwan-loop/8.jpg", alt_ja: "台中・彩虹眷村の色鮮やかな壁画の前で", alt_en: "In front of the vivid murals of Rainbow Village, Taichung" },
      { src: "images/travels/taiwan-loop/9.jpg", alt_ja: "台北101の展望台から眺めた夕焼け", alt_en: "Sunset from the Taipei 101 observatory" },
      { src: "images/travels/taiwan-loop/10.jpg", alt_ja: "夜市の臭豆腐の看板の前で、ふたりで", alt_en: "The two of us by a stinky-tofu stall at a night market" },
    ],
    description_ja: [
      '当時彼女だった妻とふたりで、台湾をぐるりと一周した8日間です。台北を起点に時計回りで島を回り、移動はほとんどを鉄道に任せました。',
      '十分では天燈を空へ放ち、九份では赤い提灯に照らされた石段の路地を歩きました。翌朝は宿から海を眺めて出発。太平洋沿いを列車で南へ下って花蓮の夜市をのぞき、台東を経て南廻線で島の南端を回り込み、高雄へ。',
      '高雄では駅の天井を覆うステンドグラスと、灯籠がずらりと並ぶ廟の夜景に息をのみ、蓮池潭の龍虎塔を眺めました。古都・台南で孔子廟を訪ね、台中の彩虹眷村に寄り道してから、最後は高速鉄道で台北へ。台北101の展望台から見た夕焼けで、島を一周する旅を締めくくりました。',
    ],
    description_en: [
      'Eight days circling Taiwan as a pair with my wife — girlfriend, back then. We set out from Taipei and went clockwise around the island, leaving nearly all the moving to the railways.',
      'At Shifen we sent a sky lantern up into the evening; in Jiufen we wandered stepped alleys under red lanterns, and the next morning looked out over the sea before setting off. A train down the Pacific coast took us to the night market in Hualien, then via Taitung along the South-Link Line around the island’s southern tip to Kaohsiung.',
      'In Kaohsiung we stood under a station ceiling made of stained glass and gaped at a temple hall lined with lanterns, then took in the Dragon and Tiger Pagodas on Lotus Pond. A visit to the Confucius Temple in the old capital, Tainan, a detour to Rainbow Village in Taichung, and finally the high-speed rail back to Taipei. A sunset from the Taipei 101 observatory closed the loop.',
    ],
    tags: [
      { ja: '鉄道', en: 'Rail', cat: 'vehicle' },
      { ja: '海外', en: 'Overseas', cat: 'region' },
      { ja: 'ふたり旅', en: 'Two of us', cat: 'theme' },
    ],
    route: 'taiwan-loop',
    routeKind: 'approx',
    datesFromLog: false,
    cities: [
      { ja: '台北', en: 'Taipei', lon: 121.517, lat: 25.048, labelFlip: true },
      { ja: '十分', en: 'Shifen', lon: 121.775, lat: 25.042, labelDy: 18 },
      { ja: '九份', en: 'Jiufen', lon: 121.845, lat: 25.108 },
      { ja: '花蓮', en: 'Hualien', lon: 121.6, lat: 23.99 },
      { ja: '台東', en: 'Taitung', lon: 121.12, lat: 22.79 },
      { ja: '高雄', en: 'Kaohsiung', lon: 120.3, lat: 22.63, labelFlip: true },
      { ja: '台南', en: 'Tainan', lon: 120.21, lat: 22.99, labelFlip: true },
      { ja: '台中', en: 'Taichung', lon: 120.68, lat: 24.14, labelFlip: true },
    ],
  },
];

export default TRAVEL_PROJECTS;
